// src/pages/misc/onboarding/priceBook.ts
// [PRICING] The screens' one source of prices and limits: the server's price book (get_public_per_seat_pricing, read through
//   perSeatApi.fetchPerSeatPricing) looked up by the designer's ids. No number is typed here or in the screens: his orderQuote.ts
//   keeps its arithmetic and takes every unit from these lookups, and the server prices the same rows by row_key (S-9, SCREEN
//   EQUALS CHARGE). Pure functions over a PriceBook value: the read's lifecycle (one read per page load, the failed-read line, the
//   retry) belongs to the screens. A lookup answering null is THE FAILED READ for the screen that needs it (S-9): a row unmapped on
//   this environment is absent from the book, never zero, and the screen shows its one line in place of the number.
import type { OrgPlanId } from '../../settings/create-organization/orgSetup';
import type { PerSeatPricing, PricingExtension, PricingLimits, PricingPlan } from './perSeatApi';

/** The server's plan words (per_seat_price_book.plans, per_seat_plan_limits.billing_plan). */
export type PlanWord = 'lite' | 'base' | 'elite';

/** A billing period in months, the server's domain. His BillingCycle maps onto it through CYCLE_MONTHS in orderQuote.ts. */
export type PeriodMonths = 1 | 6 | 12;

/** What one extension unit is, in the server's words. */
export type ExtensionScope = 'sales_user' | 'team' | 'organization' | 'account';

/** THE ID MAP (WU-138 R20): his plan ids stay his in his files; the server knows these words. */
export const PLAN_WORD: Readonly<Record<OrgPlanId, PlanWord>> = { free: 'lite', base: 'base', pro: 'elite' };

/** His plans from the cheapest up: the order the plan page and the estimate walk to find the plan that fits (S-2, S-5). */
export const PLAN_IDS_BY_PRICE: readonly OrgPlanId[] = ['free', 'base', 'pro'];

/**
 * His charge ids (the `push` literals of extensionCharges in orderQuote.ts) minus waTeam: the per-team WhatsApp API row is not
 * shown and never sent (P-4).
 */
export type ExtensionId = 'phone' | 'voice' | 'voicePro' | 'waPersonal' | 'waCompany' | 'zoom' | 'zoomTeam' | 'zoomCompany' | 'zoomOwn';

/**
 * THE ROW MAP (WU-138 R20): his id → the book's family and scope. The plan chooses the row inside the family: voz_basica is sold
 * on base and elite by one row and on lite by its own (dearer) row, and voz_avanzada has no row on lite, so his lock stands.
 */
export const EXTENSION_ROW: Readonly<Record<ExtensionId, { family: string; scope: ExtensionScope }>> = {
  phone: { family: 'phone_line', scope: 'sales_user' },
  voice: { family: 'voz_basica', scope: 'sales_user' },
  voicePro: { family: 'voz_avanzada', scope: 'sales_user' },
  waPersonal: { family: 'whatsapp_api', scope: 'sales_user' },
  waCompany: { family: 'whatsapp_api', scope: 'organization' },
  zoom: { family: 'zoom_included', scope: 'sales_user' },
  zoomTeam: { family: 'zoom_included', scope: 'team' },
  zoomCompany: { family: 'zoom_included', scope: 'organization' },
  zoomOwn: { family: 'zoom_own', scope: 'account' },
};

/** A plan row the screen prices from: the row_key P2 sends and the amount per seat, per period and per month. */
export interface PlanRow {
  rowKey: string;
  months: PeriodMonths;
  perSeatPerPeriodMxn: number;
  perSeatPerMonthMxn: number;
}

/** An extension row: the row_key P2 sends with the chosen quantity, and the amount per unit, per period and per month. */
export interface ExtensionRow {
  rowKey: string;
  family: string;
  scope: ExtensionScope;
  months: PeriodMonths;
  perUnitPerPeriodMxn: number;
  perUnitPerMonthMxn: number;
  /** The family this one needs turned on first (voz_basica and voz_avanzada need phone_line). */
  requiresFamily: string | null;
  /** Rows of one group exclude each other on the screen (voice, zoom). */
  choiceGroup: string | null;
}

/** A plan's maximums; null = no plan maximum. */
export interface PlanLimits {
  maxOrganizations: number | null;
  maxProjects: number | null;
  maxTeams: number | null;
  maxSalesUsers: number | null;
  maxAdmins: number | null;
}

/** What the structure builder counted (his draft's empresas, proyectos, equipos, asesores, admins), in the server's words. */
export interface StructureCounts {
  organizations: number;
  projects: number;
  teams: number;
  salesUsers: number;
  admins: number;
}

/** The book indexed once from the served pricing. Build it with buildPriceBook; read it with the lookups below. */
export interface PriceBook {
  /** plan word → months → row */
  readonly plans: Readonly<Record<string, Readonly<Record<number, PlanRow>>>>;
  /** extensionKey(family, scope, plan word) → months → row */
  readonly extensions: Readonly<Record<string, Readonly<Record<number, ExtensionRow>>>>;
  /** plan word → limits */
  readonly limits: Readonly<Record<string, PlanLimits>>;
}

const PERIODS: readonly number[] = [1, 6, 12];

function periodMonths(n: number): PeriodMonths | null {
  return PERIODS.indexOf(n) >= 0 ? (n as PeriodMonths) : null;
}

function isExtensionScope(s: string): s is ExtensionScope {
  return s === 'sales_user' || s === 'team' || s === 'organization' || s === 'account';
}

function extensionKey(family: string, scope: string, planWord: string): string {
  return `${family}/${scope}/${planWord}`;
}

/**
 * Indexes the served book. A row off the server's domain (a period that is not 1, 6 or 12; a scope the screens do not know) is
 * left out, so it reads as absent — THE FAILED READ for the screen that would have needed it.
 */
export function buildPriceBook(pricing: PerSeatPricing): PriceBook {
  const plans: Record<string, Record<number, PlanRow>> = {};
  pricing.plans.forEach((plan: PricingPlan) => {
    const rows: Record<number, PlanRow> = {};
    plan.periods.forEach((p) => {
      const months = periodMonths(p.months);
      if (months === null) return;
      rows[months] = {
        rowKey: p.row_key,
        months,
        perSeatPerPeriodMxn: p.per_seat_per_period_mxn,
        perSeatPerMonthMxn: p.per_seat_per_month_mxn,
      };
    });
    plans[plan.plan] = rows;
  });

  const extensions: Record<string, Record<number, ExtensionRow>> = {};
  pricing.extensions.forEach((ext: PricingExtension) => {
    if (!isExtensionScope(ext.scope)) return;
    const scope = ext.scope;
    const rows: Record<number, ExtensionRow> = {};
    ext.periods.forEach((p) => {
      const months = periodMonths(p.months);
      if (months === null) return;
      rows[months] = {
        rowKey: p.row_key,
        family: ext.family,
        scope,
        months,
        perUnitPerPeriodMxn: p.per_unit_per_period_mxn,
        perUnitPerMonthMxn: p.per_unit_per_month_mxn,
        requiresFamily: ext.requires_family,
        choiceGroup: ext.choice_group,
      };
    });
    ext.plans.forEach((word) => {
      const key = extensionKey(ext.family, scope, word);
      // The first row of a family, scope and plan wins: the server serves them in sort_order and sells one row per combination.
      if (!(key in extensions)) extensions[key] = rows;
    });
  });

  const limits: Record<string, PlanLimits> = {};
  Object.keys(pricing.limits).forEach((word) => {
    const l: PricingLimits = pricing.limits[word];
    limits[word] = {
      maxOrganizations: l.max_organizations,
      maxProjects: l.max_projects,
      maxTeams: l.max_teams,
      maxSalesUsers: l.max_sales_users,
      maxAdmins: l.max_admins,
    };
  });

  return { plans, extensions, limits };
}

/** The plan's row for the period, or null: THE FAILED READ for the screen that needs it. */
export function planRow(book: PriceBook, planId: OrgPlanId, months: PeriodMonths): PlanRow | null {
  return book.plans[PLAN_WORD[planId]]?.[months] ?? null;
}

/** The extension's row on the plan for the period, or null: not sold on that plan (voz_avanzada on lite) or THE FAILED READ. */
export function extensionRow(book: PriceBook, id: ExtensionId, planId: OrgPlanId, months: PeriodMonths): ExtensionRow | null {
  const { family, scope } = EXTENSION_ROW[id];
  return book.extensions[extensionKey(family, scope, PLAN_WORD[planId])]?.[months] ?? null;
}

/** The plan's maximums, or null: THE FAILED READ. */
export function planLimits(book: PriceBook, planId: OrgPlanId): PlanLimits | null {
  return book.limits[PLAN_WORD[planId]] ?? null;
}

function under(count: number, max: number | null): boolean {
  return max === null || count <= max;
}

/** Whether the structure sits inside every maximum the plan states (a null maximum binds nothing: admins on every plan today, S-1). */
export function fits(limits: PlanLimits, counts: StructureCounts): boolean {
  return (
    under(counts.organizations, limits.maxOrganizations) &&
    under(counts.projects, limits.maxProjects) &&
    under(counts.teams, limits.maxTeams) &&
    under(counts.salesUsers, limits.maxSalesUsers) &&
    under(counts.admins, limits.maxAdmins)
  );
}

/** Whether the plan fits the structure; null when the plan's limits are not in the book (THE FAILED READ). */
export function planFits(book: PriceBook, planId: OrgPlanId, counts: StructureCounts): boolean | null {
  const limits = planLimits(book, planId);
  return limits === null ? null : fits(limits, counts);
}

/**
 * The cheapest of his plans whose limits hold the structure, or null when none does — the builder's ceilings keep that from
 * happening (S-3) — or when the book holds no limits (THE FAILED READ; planFits tells the two apart).
 */
export function cheapestPlanThatFits(book: PriceBook, counts: StructureCounts): OrgPlanId | null {
  for (let i = 0; i < PLAN_IDS_BY_PRICE.length; i += 1) {
    if (planFits(book, PLAN_IDS_BY_PRICE[i], counts) === true) return PLAN_IDS_BY_PRICE[i];
  }
  return null;
}
